<template>
  <div class="register-view">
    <div class="register-container">
      <h2 class="title">创建账号</h2>
      <p class="subtitle">加入AiVitaHelper，开启您的求职之旅</p>
      
      <el-form 
        ref="registerFormRef"
        :model="registerForm" 
        :rules="rules" 
        class="register-form"
      >
        <el-form-item prop="account">
          <el-input
            v-model="registerForm.account"
            placeholder="账号"
            :prefix-icon="User"
          />
        </el-form-item>
        
        <el-form-item prop="username">
          <el-input
            v-model="registerForm.username"
            placeholder="用户名"
            :prefix-icon="UserFilled"
          />
        </el-form-item>
        
        <el-form-item prop="email">
          <el-input
            v-model="registerForm.email"
            placeholder="邮箱"
            :prefix-icon="Message"
          >
            <template #append>
              <el-button
                :disabled="!!countdown || loading"
                @click="handleSendCode"
              >
                {{ countdown ? `${countdown}s` : '获取验证码' }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        
        <el-form-item prop="verificationCode">
          <el-input
            v-model="registerForm.verificationCode"
            placeholder="验证码"
            :prefix-icon="Key"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="registerForm.password"
            type="password"
            placeholder="密码"
            :prefix-icon="Lock"
            show-password
          />
        </el-form-item>
        
        <el-form-item prop="confirmPassword">
          <el-input
            v-model="registerForm.confirmPassword"
            type="password"
            placeholder="确认密码"
            :prefix-icon="Lock"
            show-password
          />
        </el-form-item>
        
        <el-form-item prop="agreement" :rules="[{ required: true, message: '请阅读并同意用户协议和隐私政策', trigger: 'change' }]">
          <div class="agreement-section">
            <el-checkbox v-model="registerForm.agreement">
              我已阅读并同意
              <router-link to="/user-agreement" target="_blank" class="agreement-link">《用户协议》</router-link>
              和
              <router-link to="/privacy-policy" target="_blank" class="agreement-link">《隐私政策》</router-link>
            </el-checkbox>
          </div>
        </el-form-item>
        
        <el-button type="primary" class="submit-btn" :loading="loading" @click="handleRegister">
          注册
        </el-button>
        
        <div class="login-link">
          已有账号？
          <router-link to="/login">立即登录</router-link>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from 'vue';
import { User, UserFilled, Message, Lock, Key } from '@element-plus/icons-vue';
import { useAuthStore } from '@/store/modules/auth';
import { ElMessage } from 'element-plus';
import type { FormInstance } from 'element-plus';

export default defineComponent({
  name: 'RegisterView',
  setup() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const countdown = ref(0);
    const registerFormRef = ref<FormInstance>();
    
    const registerForm = reactive({
      account: '',
      username: '',
      email: '',
      verificationCode: '',
      password: '',
      confirmPassword: '',
      agreement: false
    });
    
    const validatePass = (rule: any, value: string, callback: (error?: Error) => void) => {
      if (value === '') {
        callback(new Error('请输入密码'));
      } else if (value.length < 6) {
        callback(new Error('密码长度不能小于6位'));
      } else {
        if (registerForm.confirmPassword !== '') {
          if (registerFormRef.value) {
            registerFormRef.value.validateField('confirmPassword');
          }
        }
        callback();
      }
    };
    
    const validatePass2 = (rule: any, value: string, callback: (error?: Error) => void) => {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== registerForm.password) {
        callback(new Error('两次输入密码不一致!'));
      } else {
        callback();
      }
    };
    
    const rules = {
      account: [
        { required: true, message: '请输入账号', trigger: 'blur' },
        { min: 3, message: '账号长度不能小于3位', trigger: 'blur' }
      ],
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' }
      ],
      email: [
        { required: true, message: '请输入邮箱', trigger: 'blur' },
        { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
      ],
      verificationCode: [
        { required: true, message: '请输入验证码', trigger: 'blur' },
        { len: 6, message: '验证码长度应为6位', trigger: 'blur' }
      ],
      password: [
        { validator: validatePass, trigger: 'blur' }
      ],
      confirmPassword: [
        { validator: validatePass2, trigger: 'blur' }
      ],
      agreement: [
        { required: true, message: '请阅读并同意用户协议和隐私政策', trigger: 'change' }
      ]
    };
    
    const startCountdown = () => {
      countdown.value = 60;
      const timer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    };
    
    const handleSendCode = async () => {
      if (!registerForm.email) {
        ElMessage.warning('请先输入邮箱地址');
        return;
      }
      
      try {
        loading.value = true;
        await authStore.sendRegisterCode(registerForm.email);
        ElMessage.success('验证码已发送，请查收邮件');
        startCountdown();
      } catch (error: any) {
        ElMessage.error(error.message || '发送验证码失败');
      } finally {
        loading.value = false;
      }
    };
    
    const handleRegister = async () => {
      if (!registerFormRef.value) return;
      
      await registerFormRef.value.validate(async (valid) => {
        if (valid) {
          if (!registerForm.agreement) {
            ElMessage.warning('请阅读并同意用户协议和隐私政策');
            return;
          }
          
          try {
            loading.value = true;
            const { agreement, ...rest } = registerForm;
            const registerData = {
              ...rest,
              phone: '' // 添加空的phone字段以满足接口要求
            };
            await authStore.register(registerData);
            ElMessage.success('注册成功');
          } catch (error: any) {
            ElMessage.error(error.message || '注册失败');
          } finally {
            loading.value = false;
          }
        }
      });
    };
    
    return {
      registerForm,
      registerFormRef,
      rules,
      loading,
      countdown,
      handleSendCode,
      handleRegister,
      User,
      UserFilled,
      Message,
      Lock,
      Key
    };
  }
});
</script>

<style lang="scss" scoped>
.register-view {
  min-height: calc(100vh - 85px - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f5f5f7;
}

.register-container {
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 8px;
  text-align: center;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 32px;
  text-align: center;
}

.register-form {
  .el-input {
    --el-input-height: 44px;
    margin-bottom: 16px;
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 1rem;
  border-radius: 22px;
  margin-bottom: 20px;
}

.login-link {
  text-align: center;
  font-size: 0.9rem;
  color: #666;
  
  a {
    color: var(--el-color-primary);
    margin-left: 4px;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.agreement-section {
  margin-bottom: 20px;
  
  .el-checkbox {
    font-size: 14px;
    color: #606266;
    
    .agreement-link {
      color: var(--el-color-primary);
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style> 